import React, { useState } from 'react';
import { Alert, Button, Checkbox, Col, Form, Input, Modal, Row, Switch } from 'antd';
import { FctDato, fmtFechaHora, fmtNumero } from '../../fct/react';
import {
  actualizarInforme, crearInforme, mensajeDeError, type DatosInforme, type InformeCatalogo,
} from '../../api/reportesApi';
import AccesosInforme from './AccesosInforme';
import BorrarInforme from './BorrarInforme';
import './FormularioInforme.css';

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NO_ES_GUID = 'No es un GUID válido, con el formato 00000000-0000-0000-0000-000000000000.';

const FILTROS: { campo: keyof DatosInforme; etiqueta: string }[] = [
  { campo: 'filtroDelegados', etiqueta: 'Delegados' },
  { campo: 'filtroDelegaciones', etiqueta: 'Delegaciones' },
  { campo: 'filtroGerenciasZona', etiqueta: 'Gerencias de zona' },
  { campo: 'filtroProvincias', etiqueta: 'Provincias' },
];

const VALORES_NUEVO: Partial<DatosInforme> = { activo: true };

const segundos = (valor?: number | null) => (valor === null || valor === undefined ? null : `${fmtNumero(valor)} s`);

interface Props {
  /** Con informe, lo edita; sin él, da de alta uno nuevo. */
  informe: InformeCatalogo | null;
  onCerrar: () => void;
  onGuardado: () => void;
}

/**
 * El formulario de mantenimiento de un informe (#411). El primer dato es si está activo: la baja es eso, y borrar es otra cosa,
 * con su propio aviso. Lo que rechaza la API (un nombre o un GUID repetido) se enseña aquí, sin cerrar el formulario. La duración
 * de los refrescos y el alta son de solo lectura: se muestran como dato.
 */
const FormularioInforme: React.FC<Props> = ({ informe, onCerrar, onGuardado }) => {
  const [form] = Form.useForm<DatosInforme>();
  const accesoGeneral = Form.useWatch('accesoGeneral', form) ?? informe?.accesoGeneral ?? false;
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [borrando, setBorrando] = useState(false);

  const guardar = async (valores: DatosInforme) => {
    setGuardando(true);
    setError(null);
    try {
      await (informe ? actualizarInforme(informe.id, valores) : crearInforme(valores));
      onGuardado();
    } catch (e) {
      setError(mensajeDeError(e, 'No se ha podido guardar el informe. Inténtalo de nuevo en unos minutos.'));
      setGuardando(false);
    }
  };

  return (
    <Modal
      open
      onCancel={onCerrar}
      title={informe ? `Mantenimiento de ${informe.titulo ?? informe.nombre ?? `informe ${informe.id}`}` : 'Nuevo informe'}
      width={820}
      footer={null}
      className="reportes-form"
    >
      <Form<DatosInforme>
        form={form}
        layout="vertical"
        initialValues={informe ?? VALORES_NUEVO}
        onFinish={guardar}
        requiredMark={false}
      >
        <Form.Item name="activo" label="Activo" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="nombre"
              label="Nombre, que va en el enlace"
              rules={[{ required: true, whitespace: true, message: 'El nombre es obligatorio.' }]}
            >
              <Input maxLength={255} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="titulo" label="Título">
              <Input maxLength={255} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="responsable" label="Responsable">
              <Input maxLength={255} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="guidReporte"
              label="GUID del informe"
              rules={[
                { required: true, whitespace: true, message: 'El GUID del informe es obligatorio.' },
                { pattern: GUID, message: NO_ES_GUID },
              ]}
            >
              <Input className="fct-mono" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="guidDataset2" label="GUID del segundo dataset" rules={[{ pattern: GUID, message: NO_ES_GUID }]}>
              <Input className="fct-mono" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="leyendaDataset2" label="Leyenda del segundo dataset">
              <Input maxLength={25} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item name="accesoGeneral" label="Acceso general" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name="botonActualizar" label="Botón Actualizar" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name="botonInfo" label="Botón info" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>
        <fieldset className="reportes-form__filtros">
          <legend>Filtros del informe</legend>
          {FILTROS.map(({ campo, etiqueta }) => (
            <Form.Item key={campo} name={campo} valuePropName="checked" noStyle>
              <Checkbox>{etiqueta}</Checkbox>
            </Form.Item>
          ))}
        </fieldset>
        <Form.Item name="filtroPersonalizado" label="Filtro personalizado">
          <Input.TextArea rows={3} className="fct-mono" />
        </Form.Item>
        {informe && (
          <Row gutter={16} className="reportes-form__lectura">
            <Col xs={12} md={6}><FctDato etiqueta="Último refresco" valor={segundos(informe.segundosActualizacion)} mono /></Col>
            <Col xs={12} md={6}><FctDato etiqueta="Último refresco del segundo" valor={segundos(informe.segundosActualizacion2)} mono /></Col>
            <Col xs={12} md={6}><FctDato etiqueta="Fecha de alta" valor={informe.fechaAlta ? fmtFechaHora(informe.fechaAlta) : null} mono /></Col>
            <Col xs={12} md={6}><FctDato etiqueta="Alta por" valor={informe.usuarioAlta} /></Col>
          </Row>
        )}
        {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
        <div className="reportes-form__pie">
          {informe && <Button danger className="reportes-form__borrar" onClick={() => setBorrando(true)}>Eliminar informe</Button>}
          <Button onClick={onCerrar}>Cancelar</Button>
          <Button type="primary" htmlType="submit" loading={guardando}>{informe ? 'Guardar' : 'Crear informe'}</Button>
        </div>
      </Form>
      {!accesoGeneral && (informe
        ? <AccesosInforme idInforme={informe.id} />
        : <p className="reportes-form__ayuda">Los accesos nominales se añaden una vez creado el informe.</p>)}
      {informe && borrando && <BorrarInforme informe={informe} onCerrar={() => setBorrando(false)} onBorrado={onGuardado} />}
    </Modal>
  );
};

export default FormularioInforme;
